import React from 'react';
import ReactTooltip from 'react-tooltip';
import Button from 'react-bootstrap/Button';
import { Spinner } from 'react-bootstrap';
import ValidationSection from './ValidationSection';
import { useBulletin } from '../../../context/bulletin';
import DailyReportModal from './DailyReportModal';

interface ValidationButtonProps {
  userIsAuthenticated: boolean;
  userHasBulletinPermission: boolean;
}

function ValidationButton({
  userIsAuthenticated,
  userHasBulletinPermission,
}: ValidationButtonProps) {
  const [modalValidationIsOpen, setModalValidationIsOpen] =
    React.useState(false);
  const [dailyReportModalShow, setDailyReportModalShow] = React.useState(false);

  const { isLatestBulletin, bulletinResponse, isFetching } = useBulletin();

  if (!isLatestBulletin || !bulletinResponse?.bulletin.validation_enabled) {
    return null;
  }

  if (!userIsAuthenticated) {
    return <p>You should be connected to validate a bulletin</p>;
  }

  if (!userHasBulletinPermission) {
    return <p>You don't have enough rights to validate a bulletin</p>;
  }

  const isBulletinReadyToSend = bulletinResponse.progress_percent === 100;

  const cannotValidateBulletin =
    !bulletinResponse.bulletin.isValidated && !isBulletinReadyToSend;
  const canValidateBulletin =
    !bulletinResponse.bulletin.isValidated && isBulletinReadyToSend;
  // the same button is used to validate the bulletin
  // or access the daily report once the bulletin has been validated
  const isValidationOrReportButtonActive =
    bulletinResponse.bulletin.isValidated || canValidateBulletin;

  const cannotValidateMsg =
    'You should see all maps before validating the bulletin';

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div
        data-tip={cannotValidateBulletin ? cannotValidateMsg : ''}
        data-for="cannot_validate"
      >
        <Button
          data-cy="validation-button"
          onClick={() => {
            if (bulletinResponse.bulletin.isValidated) {
              setDailyReportModalShow(true);
            } else {
              setModalValidationIsOpen(true);
            }
          }}
          disabled={!isValidationOrReportButtonActive || isFetching}
          style={{ width: '100%' }}
          variant={
            bulletinResponse.bulletin.has_messages ? 'danger' : 'primary'
          }
        >
          {isFetching && (
            <span style={{ paddingRight: 6 }}>
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </span>
          )}
          {bulletinResponse.bulletin.isValidated
            ? 'Daily report'
            : 'Validation'}
        </Button>
      </div>
      <ReactTooltip
        id="cannot_validate"
        effect="solid"
        delayHide={1500}
        className="smalltooltip"
      />

      {isBulletinReadyToSend && (
        <ValidationSection
          show={modalValidationIsOpen}
          onClose={() => setModalValidationIsOpen(false)}
        />
      )}
      {bulletinResponse.bulletin.isValidated && (
        <DailyReportModal
          show={dailyReportModalShow}
          onClose={() => setDailyReportModalShow(false)}
        />
      )}
    </div>
  );
}

export default ValidationButton;
